import { useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { trackAnalyticsEvents, trackAnalyticsSession } from "@/lib/api";

const SESSION_KEY = "dn_analytics_sid";
const HEARTBEAT_INTERVAL = 60000; // 1 minute
const BATCH_INTERVAL = 3000; // 3 seconds

function getSessionId() {
  let sid = sessionStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = "s_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
}

function getUtmParams() {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get("utm_source") || undefined,
    utm_medium: params.get("utm_medium") || undefined,
    utm_campaign: params.get("utm_campaign") || undefined,
  };
}

// Singleton event queue
let eventQueue = [];
let flushTimer = null;

function flushEvents() {
  if (eventQueue.length === 0) return;
  const batch = [...eventQueue];
  eventQueue = [];
  trackAnalyticsEvents(batch);
}

function queueEvent(event) {
  eventQueue.push({
    ...event,
    timestamp: new Date().toISOString(),
    session_id: getSessionId(),
  });
  if (!flushTimer) {
    flushTimer = setTimeout(() => {
      flushTimer = null;
      flushEvents();
    }, BATCH_INTERVAL);
  }
}

// Track a named event
export function trackEvent(eventName, data = {}) {
  queueEvent({ event_name: eventName, ...data });
}

export function useAnalytics(user) {
  const location = useLocation();
  const lastRoute = useRef("");
  const heartbeatRef = useRef(null);
  const sessionStarted = useRef(false);

  // Session start + heartbeat
  useEffect(() => {
    const sid = getSessionId();
    const utm = getUtmParams();
    if (!sessionStarted.current) {
      sessionStarted.current = true;
      trackAnalyticsSession({
        session_id: sid,
        user_id: user?.email,
        referrer: document.referrer || undefined,
        ...utm,
      });
      queueEvent({ event_name: "session_start", referrer: document.referrer });
    }

    heartbeatRef.current = setInterval(() => {
      trackAnalyticsSession({ session_id: sid, user_id: user?.email });
    }, HEARTBEAT_INTERVAL);

    // Flush on unload
    const handleUnload = () => flushEvents();
    window.addEventListener("beforeunload", handleUnload);

    return () => {
      clearInterval(heartbeatRef.current);
      window.removeEventListener("beforeunload", handleUnload);
    };
  }, [user]);

  // Page view tracking on route change
  useEffect(() => {
    const route = location.pathname;
    if (route === lastRoute.current) return;
    lastRoute.current = route;
    queueEvent({
      event_name: "page_view",
      route,
      referrer: document.referrer || undefined,
      ...getUtmParams(),
    });
  }, [location.pathname]);
}
